import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { useLanguage } from 'lib/i18n';
import api, { getApiErrorMessage } from 'lib/api';
import { formatDateTime } from 'lib/format';
import { toast } from 'sonner';
import { Mail, Eye, Send, RefreshCw, Users, Info } from 'lucide-react';

const initialCampaign = {
  subject_fr: 'Une offre spéciale vous attend chez ColiBuddy',
  body_fr: 'Bonjour {{first_name}},\n\nDécouvrez nos nouveautés et profitez de cette offre exclusive.\n\nÀ bientôt,\nL’équipe ColiBuddy',
  subject_en: 'A special offer is waiting for you at ColiBuddy',
  body_en: 'Hello {{first_name}},\n\nDiscover what’s new and enjoy this exclusive offer.\n\nSee you soon,\nThe ColiBuddy team',
  cta_label_fr: 'Découvrir l’offre',
  cta_label_en: 'Discover the offer',
  cta_url: '',
};

export default function AdminCampaignsPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState(initialCampaign);
  const [preview, setPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [retrying, setRetrying] = useState('');

  const loadCampaigns = async () => {
    try {
      const response = await api.get('/admin/campaigns');
      setCampaigns(response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('adminCampaigns.loadError')));
    }
  };

  useEffect(() => { loadCampaigns(); }, []);

  const update = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
    setPreview(null);
  };

  const loadPreview = async () => {
    setPreviewBusy(true);
    try {
      const response = await api.post('/admin/campaigns/preview', form);
      setPreview(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('adminCampaigns.previewError')));
    } finally {
      setPreviewBusy(false);
    }
  };

  const sendCampaign = async () => {
    if (!preview) {
      toast.error(t('adminCampaigns.previewFirst'));
      return;
    }
    const confirmMessage = t('adminCampaigns.confirmSend').replace('{count}', String(preview.audience_count));
    if (!window.confirm(confirmMessage)) return;
    setSendBusy(true);
    try {
      const response = await api.post('/admin/campaigns', form);
      toast.success(t('adminCampaigns.queued').replace('{count}', String(response.data.audience_total || 0)));
      setPreview(null);
      await loadCampaigns();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('adminCampaigns.sendError')));
    } finally {
      setSendBusy(false);
    }
  };

  const retryFailed = async (campaignId) => {
    setRetrying(campaignId);
    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/retry-failed`);
      toast.success(t('adminCampaigns.retryQueued').replace('{count}', String(response.data.queued)));
      await loadCampaigns();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('adminCampaigns.retryError')));
    } finally {
      setRetrying('');
    }
  };

  const statusLabel = (status) => t(`adminCampaigns.status.${status}`) || status;

  return (
    <DashboardShell>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Mail size={21} /></div>
        <div>
          <h1 className="text-2xl font-bold">{t('adminCampaigns.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('adminCampaigns.description')}</p>
        </div>
      </div>

      <Alert className="mb-5">
        <Info className="h-4 w-4" />
        <AlertTitle>{t('adminCampaigns.consentTitle')}</AlertTitle>
        <AlertDescription>{t('adminCampaigns.consentDescription')}</AlertDescription>
      </Alert>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(340px,0.8fr)]">
        <Card>
          <CardHeader><CardTitle>{t('adminCampaigns.compose')}</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="campaign-subject-fr">{t('adminCampaigns.subjectFr')}</Label>
                <Input id="campaign-subject-fr" maxLength={140} value={form.subject_fr} onChange={event => update('subject_fr', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="campaign-subject-en">{t('adminCampaigns.subjectEn')}</Label>
                <Input id="campaign-subject-en" maxLength={140} value={form.subject_en} onChange={event => update('subject_en', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="campaign-body-fr">{t('adminCampaigns.bodyFr')}</Label>
                <Textarea id="campaign-body-fr" maxLength={5000} rows={9} value={form.body_fr} onChange={event => update('body_fr', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="campaign-body-en">{t('adminCampaigns.bodyEn')}</Label>
                <Textarea id="campaign-body-en" maxLength={5000} rows={9} value={form.body_en} onChange={event => update('body_en', event.target.value)} />
              </div>
            </div>

            <div className="rounded-lg border bg-secondary/30 p-4">
              <h2 className="mb-3 text-sm font-semibold">{t('adminCampaigns.buttonTitle')}</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2"><Label htmlFor="campaign-cta-fr">{t('adminCampaigns.buttonFr')}</Label><Input id="campaign-cta-fr" maxLength={80} value={form.cta_label_fr} onChange={event => update('cta_label_fr', event.target.value)} /></div>
                <div className="space-y-2"><Label htmlFor="campaign-cta-en">{t('adminCampaigns.buttonEn')}</Label><Input id="campaign-cta-en" maxLength={80} value={form.cta_label_en} onChange={event => update('cta_label_en', event.target.value)} /></div>
                <div className="space-y-2"><Label htmlFor="campaign-cta-url">{t('adminCampaigns.buttonUrl')}</Label><Input id="campaign-cta-url" type="url" placeholder="https://..." value={form.cta_url} onChange={event => update('cta_url', event.target.value)} /></div>
              </div>
            </div>

            <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
              {t('adminCampaigns.personalization')}
              <div className="mt-1 font-mono text-xs">{'{{first_name}}'} · {'{{full_name}}'}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={loadPreview} disabled={previewBusy} data-testid="campaign-preview-button">
                <Eye size={15} className="mr-2" />{previewBusy ? t('adminCampaigns.loadingPreview') : t('adminCampaigns.preview')}
              </Button>
              <Button onClick={sendCampaign} disabled={!preview || sendBusy || !preview.audience_count} data-testid="campaign-send-button">
                <Send size={15} className="mr-2" />{sendBusy ? t('adminCampaigns.sending') : t('adminCampaigns.send')}
              </Button>
            </div>
            {preview && (
              <div className="space-y-4" data-testid="campaign-preview-result">
                <Alert>
                  <Users className="h-4 w-4" />
                  <AlertTitle>{t('adminCampaigns.audience').replace('{count}', String(preview.audience_count))}</AlertTitle>
                  <AlertDescription>{t('adminCampaigns.audienceNote')}</AlertDescription>
                </Alert>
                <div className="grid gap-3 md:grid-cols-2">
                  {[[t('adminCampaigns.sampleFr'), preview.preview_fr], [t('adminCampaigns.sampleEn'), preview.preview_en]].map(([label, sample]) => (
                    <div key={label} className="rounded-lg border p-4">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
                      <div className="font-semibold">{sample.subject}</div>
                      <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-sm text-muted-foreground">{sample.body}</pre>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>{t('adminCampaigns.history')}</CardTitle>
            <Button variant="ghost" size="icon" onClick={loadCampaigns} aria-label={t('adminCampaigns.refresh')}><RefreshCw size={16} /></Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {!campaigns.length && <p className="text-sm text-muted-foreground">{t('adminCampaigns.noCampaigns')}</p>}
            {campaigns.map(campaign => (
              <div key={campaign.id} className="rounded-lg border p-3" data-testid="campaign-history-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium">{campaign.subject_fr}</div>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-1 text-xs">{statusLabel(campaign.status)}</span>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">{formatDateTime(campaign.created_at)}</div>
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                  <span>{t('adminCampaigns.sent')}: {campaign.sent_count || 0}</span>
                  <span>{t('adminCampaigns.failed')}: {campaign.failed_count || 0}</span>
                  <span>{t('adminCampaigns.suppressed')}: {campaign.suppressed_count || 0}</span>
                  <span>{t('adminCampaigns.recipients')}: {campaign.audience_total || 0}</span>
                </div>
                {campaign.failed_count > 0 && (
                  <Button size="sm" variant="outline" className="mt-3" disabled={retrying === campaign.id} onClick={() => retryFailed(campaign.id)}>
                    <RefreshCw size={13} className="mr-1.5" />{retrying === campaign.id ? t('adminCampaigns.retrying') : t('adminCampaigns.retry')}
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}
